import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import constants from 'core/constants';
import { CombinedStates } from 'core/types';
import { buildMainToolbar } from 'core/utils/mainToolbar';
import './newUpdateBanner.scss';

// Strict YYYY-MM-DD parsing, so a malformed env value is rejected rather than
// being coerced into some other date.
dayjs.extend(customParseFormat);

/**
 * Go-live date, as YYYY-MM-DD, from REACT_APP_FEATURE_RELEASE_DATE.
 *
 * Config rather than a constant so release can set it per environment without a
 * code change, and so nothing is baked in that would be wrong the moment it is
 * merged. The banner counts its 10 days from here, which means it disappears for
 * everyone at the same moment rather than following each user around.
 */
export const RELEASE_DATE = constants.FEATURE_RELEASE_DATE;

/** How long the announcement stays up after the release. */
export const VISIBLE_FOR_DAYS = 10;

/**
 * Whether the announcement is still inside its window.
 *
 * Compared on whole days so the banner is up for all of the tenth day rather
 * than vanishing part-way through it depending on the time someone logs in.
 *
 * An unset or unparseable release date returns false: no banner. Being silent
 * is the right way to be misconfigured here, since the alternative is running
 * the announcement over the wrong dates with nobody noticing.
 *
 * @param now         Injectable for tests; defaults to today.
 * @param releaseDate Injectable for tests; defaults to the configured date.
 */
export const isWithinAnnouncementWindow = (
    now: dayjs.Dayjs = dayjs(),
    releaseDate: string = RELEASE_DATE,
): boolean => {
    if (!releaseDate) return false;

    const release = dayjs(releaseDate, 'YYYY-MM-DD', true);
    if (!release.isValid()) return false;

    const endsAfter = release.startOf('day').add(VISIBLE_FOR_DAYS, 'day');
    return now.startOf('day').isBefore(endsAfter);
};

/**
 * What's new, keyed by the nav item each thing lives behind.
 *
 * Announcing something a role cannot open is worse than saying nothing, so the
 * list is filtered against that role's real toolbar — the same source of truth
 * the feature tour uses. Change the visibility rules in mainToolbar.ts and this
 * follows automatically.
 */
const ANNOUNCEMENTS: Array<{ tourId: string; text: string }> = [
    { tourId: 'nav-useful-contacts', text: 'Useful Contacts' },
    { tourId: 'nav-sites', text: 'a site details page for every site' },
    { tourId: 'nav-incidents', text: 'incident reporting' },
];

/** "a", "a and b", "a, b, and c". */
export const joinFeatures = (items: string[]): string => {
    if (items.length <= 1) return items[0] ?? '';
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

/**
 * The parts of the announcement this role should actually be told about.
 *
 * Returns an empty feature list when there is nothing they can reach — security
 * users have none of these areas, so the banner does not render for them at all.
 */
export const buildAnnouncement = (accessLevel?: string): { features: string; canBook: boolean } => {
    const toolbarTourIds = new Set(
        buildMainToolbar(accessLevel).map((item) => item.tourId).filter(Boolean) as string[],
    );

    const features = ANNOUNCEMENTS
        .filter(({ tourId }) => toolbarTourIds.has(tourId))
        .map(({ text }) => text);

    return {
        features: joinFeatures(features),
        // The booking sentence is site-related, so it only applies to roles that
        // have the Sites area.
        canBook: toolbarTourIds.has('nav-sites'),
    };
};

/**
 * "What's new" announcement on the Dashboard, styled to match the Sites page
 * info banner.
 *
 * Closing it hides it for the current visit only (same as the Sites banner) —
 * it returns on the next load until the window closes on its own.
 */
const NewUpdateBanner: FC = () => {
    const [isVisible, setIsVisible] = useState<boolean>(true);
    const { userData } = useSelector((state: CombinedStates) => state.user);

    const { features, canBook } = useMemo(
        () => buildAnnouncement(userData?.role?.accessLevel),
        [userData?.role?.accessLevel],
    );

    if (!isVisible || !features || !isWithinAnnouncementWindow()) return null;

    return (
        <div className="new-update-banner">
            <p>
                <strong>A new update is available.</strong>
                {` We have added ${features}.`}
                {canBook && ' Select Create New Request below to begin a booking, or open a site '
                    + 'from the Sites list to view its details and book from there.'}
            </p>
            <button
                type="button"
                className="info-banner-close"
                onClick={() => setIsVisible(false)}
                aria-label="Close information banner">
                <svg width="14" height="15" viewBox="0 0 14 15" fill="none" aria-hidden="true">
                    <path d="M13 1.5L1 13.5M1 1.5L13 13.5" stroke="#2B2D42" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
        </div>
    );
};

export default NewUpdateBanner;
